import { Controller } from 'react-hook-form';
import { useMemo, useState } from 'react';
import copy from 'copy-to-clipboard';
import SidepanelDefaultRow from '../side-panel/side-panel-default-row';
import DateInput from '../date-input/date-input';
import { PickerPosition } from '../date-input/types';
import { RowProps } from './types';
import IconCopy from '../icons/copy-two';

import styles from './transaction.module.css';

const DateContent = ({
  title,
  value,
  onChange,
  placeholder,
  error,
  borderless,
  tabIndex,
  disabled,
  startDate,
  withShortcut,
  withTime,
  setWithTime,
}: {
  title: string;
  value: any;
  onChange: (data?: string) => void;
  placeholder?: string;
  error?: string;
  borderless?: boolean;
  tabIndex?: number;
  disabled?: boolean;
  startDate?: Date;
  withShortcut?: boolean;
  withTime?: boolean;
  setWithTime?: (data: boolean) => void;
}) => {
  const content = useMemo(() => {
    let shortcuts;

    if (withShortcut) {
      shortcuts = (
        <>
          <button type="button" className={styles.shortcutButton}>
            Today
          </button>
          <button type="button" className={styles.shortcutButton}>
            Yesterday
          </button>
        </>
      );
    }

    const data = (
      <DateInput
        title={title}
        value={value}
        onChange={(e) => onChange(e.toISOString())}
        placeholder={placeholder}
        pickerPisition={PickerPosition.RIGHT}
        className={`${styles.transactionDateInput} ${
          error ? styles.transactionDateInputError : ''
        }`}
        borderless={borderless}
        onReset={() => onChange('')}
        tabIndex={tabIndex}
        error={error}
        disabled={disabled}
        startDate={startDate}
        shortcuts={shortcuts}
        onTimeClick={setWithTime}
        withTime={withTime}
        withTimeSelector
        fullWidth
        withoutCustomSelector
      />
    );

    return data;
  }, [
    borderless,
    disabled,
    error,
    onChange,
    placeholder,
    startDate,
    tabIndex,
    title,
    value,
    withShortcut,
    withTime,
  ]);

  return content;
};

export default function SidepanelDateRow({
  title,
  name,
  value,
  control,
  enableEdit,
  placeholder,
  borderless = false,
  error,
  disabled,
  startDate,
  tabIndex,
  copyValue,
  copyEnabled,
  withShortcut,
  withTime,
  clearErrors,
  setWithTime,
}: RowProps): React.JSX.Element {
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);

  const onCopy = () => {
    setCopyIsSuccess(true);
    copy(copyValue as string);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  let content;

  if (enableEdit) {
    content = (
      <Controller
        name={name}
        control={control}
        rules={{ required: false }}
        defaultValue={value}
        render={({ field }) => (
          <DateContent
            title={title}
            value={field.value}
            onChange={(e) => {
              field.onChange(e);

              if (error && clearErrors) {
                clearErrors([name]);
              }
            }}
            placeholder={placeholder}
            error={error}
            borderless={borderless}
            tabIndex={tabIndex}
            disabled={disabled}
            startDate={startDate}
            withShortcut={withShortcut}
            withTime={withTime}
            setWithTime={setWithTime}
          />
        )}
      />
    );
  } else {
    content = value;
  }

  return (
    <SidepanelDefaultRow
      title={enableEdit ? undefined : title}
      valueClassName={`${copyEnabled && enableEdit ? styles.rowWithCopy : ''} ${
        copyEnabled && !enableEdit ? styles.readCopyButtonWrapper : ''
      }`}
    >
      {content}
      {copyEnabled && (
        <>
          {enableEdit && (
            <button
              type="button"
              className={`${styles.copyButton} ${styles.dateCopyButton} ${
                copyIsSuccess ? styles.success : ''
              }`}
              onClick={onCopy}
              aria-label="Copy"
            >
              <IconCopy />
            </button>
          )}
          {!enableEdit && (
            <button
              type="button"
              className={`${styles.readCopyButton} ${
                copyIsSuccess ? styles.success : ''
              }`}
              onClick={onCopy}
              aria-label="Copy"
            >
              <IconCopy />
            </button>
          )}
        </>
      )}
    </SidepanelDefaultRow>
  );
}
